import React, { useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import StarIcon from '@mui/icons-material/Star';
import './phonics/Phonics.css';

import PhonicsSong from './phonics/PhonicsSong';
import PhonicsWordVideos from './phonics/PhonicsWordVideos';
import PhonicsStory from './phonics/PhonicsStory';
import PhonicsThanks from './phonics/PhonicsThanks';
import RewardEffect from './phonics/RewardEffect';
import { usePhonicsNavigation } from './phonics/usePhonicsNavigation';

/**
 * 步骤定义（从 letterData 动态推导，不再硬编码）
 * 每个步骤：{ key, label, renderFn }
 *
 * 统一结构：
 * songVideo (Watch) → wordVideos → storySections[] → songVideo (Chant) → Thanks
 */
function buildSteps(data) {
  const steps = [
    { key: 'song', label: 'Watch', renderFn: (mb) => (
      <PhonicsSong data={data.songVideo} poster={data.coverPoster} title="Step 1: Watch the Video" titleCn="看视频" instructionAudio={`${mb}/audio_instruction_watch.mp3`} inlineCn />
    )},
    ...(data.wordVideos ? [{ key: 'wordVideos', label: 'Watch Videos', renderFn: (mb) => (
      <PhonicsWordVideos videos={data.wordVideos} mediaBase={mb} />
    )}] : []),
    // storySections 统一包含原 vocabSections + soundSection + storySections 的内容
    ...(data.storySections || []).map((section, i) => ({
      key: `story${i}`,
      label: section.label || `Step ${i + 1}`,
      renderFn: (mb) => (
        <PhonicsStory
          data={section}
          title={section.title || section.label}
          mediaBase={mb}
          layout={section.layout}
        />
      )
    })),
  ];

  steps.push(
    { key: 'chant', label: 'Chant', renderFn: (mb) => (
      <PhonicsSong data={data.songVideo} poster={data.coverPoster} title="Chant Along with the Video" titleCn="跟着唱" instructionAudio={`${mb}/audio_instruction_chant.mp3`} inlineCn />
    )},
    { key: 'thanks', label: '完成', renderFn: (mb) => (
      <PhonicsThanks mediaBase={mb} />
    )}
  );

  return steps;
}

/**
 * Phonics AtoZ 通用课程页面
 * 接收 letterData prop，调用方必须传入
 */
function PhonicsAtoZ({ letterData: data }) {
  const STEPS = buildSteps(data);
  const { currentStep, setCurrentStep, isFullscreen, goNext, goPrev, toggleFullscreen, isFirst, isLast } = usePhonicsNavigation(STEPS);
  const mb = data.mediaBase;
  const [rewardTrigger, setRewardTrigger] = useState(0);

  return (
    <div className="phonics-root">
      {/* 奖励效果 */}
      <RewardEffect
        key={rewardTrigger}
        trigger={rewardTrigger > 0}
        onDone={() => setRewardTrigger(0)}
      />

      {/* 主内容区 */}
      <div className="phonics-content">
        {STEPS[currentStep].renderFn(mb)}
      </div>

      {/* 底部导航栏：箭头 + 进度条 + 箭头 */}
      <div className="phonics-footer">
        <Tooltip title="上一步">
          <IconButton
            size="small"
            onClick={goPrev}
            disabled={isFirst}
            className="phonics-nav-btn"
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <div className="phonics-progress">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.key}>
              <div
                className={`phonics-progress-dot ${
                  idx === currentStep ? 'active' : ''
                }`}
                title={step.label}
                onClick={() => setCurrentStep(idx)}
                style={{ cursor: 'pointer' }}
              />
              {idx < STEPS.length - 1 && (
                <div className="phonics-progress-line" />
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Tooltip title="太棒了！">
            <IconButton size="small" onClick={() => setRewardTrigger(t => t + 1)} className="phonics-nav-btn" sx={{ color: '#FFD700' }}>
              <StarIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
            <IconButton size="small" onClick={toggleFullscreen} className="phonics-nav-btn">
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="下一步">
            <IconButton
              size="small"
              onClick={goNext}
              disabled={isLast}
              className="phonics-nav-btn"
            >
              <ArrowForwardIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

export default PhonicsAtoZ;
